import { useWalkthroughLaunch } from '@/composables/useWalkthroughLaunch';
import { useTutorialsPanelStore } from '@/stores/useTutorialsPanelStore';
import { onboardingTopicContentTypes } from '@/constants/onboardingChecklist';
import { canLaunchOnboardingTopic, findOnboardingTopic } from '@/composables/useOnboardingTopics';

/**
 * The one click-dispatch point behind both checklist surfaces (`GetStarted/Widget.vue` and
 * `Tutorials/Panel.vue`): reads a topic's content type and starts the matching thing, so
 * neither surface carries its own copy of the branch.
 *
 * Marking the checklist item complete is deliberately not here — a walkthrough credits its
 * topic from `onFinalStepViewed` (`useWalkthroughLaunch.js`) and a static modal from its last
 * slide displaying (`Tutorials/StaticModal.vue`), so completion can't be missed by a surface
 * that forgets to wire it.
 */
export const useOnboardingTopicLaunch = () => {
  const { launchWalkthrough } = useWalkthroughLaunch();
  const tutorialsPanelStore = useTutorialsPanelStore();

  const launchTopic = async (topicId, { source } = {}) => {
    if (!canLaunchOnboardingTopic(topicId)) {
      return;
    }

    if (onboardingTopicContentTypes[topicId] !== 'walkthrough') {
      tutorialsPanelStore.openStaticTutorial(topicId);
      return;
    }

    await launchWalkthrough(findOnboardingTopic(topicId), {
      source,
      // Onboarding walkthroughs navigate (TMS and fleet both end on a carrier-profile route),
      // so completion returns to the job list. Completion only: a carrier who cancelled out on
      // that page may well have cancelled in order to use it.
      onComplete: () => navigateTo('/'),
    });
  };

  return { canLaunchTopic: canLaunchOnboardingTopic, launchTopic };
};
